import React, { useState, useEffect } from "react";
import {
  getAllPartenaires,
  deletePartenaire,
  updatePartenaireOrder,
  updatePartenaireActivation,
  updatePartenairePhoto,
} from "../../../services/partenairesServices";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaPlus, FaSave, FaTimes } from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";
import CreatePartenaireModal from "./CreatePartenaireModal";
import EditPartenaireModal from "./EditPartenaireModal";
import PartenairesCarousel from "./../../Home/PartenairesCarousel";


const SettingsPartenaires = () => {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [selectedPartenaire, setSelectedPartenaire] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchPartenaires();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPartenaires = async () => {
    setLoading(true);
    try {
      const data = await getAllPartenaires();
      setPartenaires(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des partenaires", error);
      toast.error("Impossible de charger les partenaires.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartenaire = async (partenaire) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${partenaire.nom} ?`)) {
      try {
        await deletePartenaire(partenaire.id);
        toast.success("Partenaire supprimé avec succès");
        fetchPartenaires();
      } catch (error) {
        toast.error("Erreur lors de la suppression d’un partenaire");
        console.error("Erreur lors de la suppression d’un partenaire", error);
      }
    }
  };

  const handleToggleActive = async (partenaire) => {
    try {
      await updatePartenaireActivation(partenaire.id, { is_active: !partenaire.is_active });
      toast.success("Statut du partenaire mis à jour avec succès");
      fetchPartenaires();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut du partenaire");
      console.error("Erreur lors de la mise à jour du statut du partenaire", error);
    }
  };

  const openEditModal = (partenaire) => {
    setSelectedPartenaire(partenaire);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setSelectedPartenaire(null);
    setIsEditModalOpen(false);
  };

  const openUploadModal = (partenaire) => {
    setSelectedPartenaire(partenaire);
    setSelectedFile(null);
    setIsUploadModalOpen(true);
  };
  const closeUploadModal = () => {
    setSelectedPartenaire(null);
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedPartenaire) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      await updatePartenairePhoto(selectedPartenaire.id, selectedFile, token);
      await fetchPartenaires();
      toast.success("Logo mis à jour avec succès !");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image :", error);
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setIsUploading(false);
    }
  };

  const movePartenaireUp = async (index) => {
    if (index === 0) return;
    const newPartenaires = [...partenaires];
    const tmp = newPartenaires[index - 1];
    newPartenaires[index - 1] = newPartenaires[index];
    newPartenaires[index] = tmp;
    newPartenaires[index - 1].ordre--;
    newPartenaires[index].ordre++;
    setPartenaires(newPartenaires);
    try {
      await updatePartenaireOrder(newPartenaires);
      fetchPartenaires();
      toast.success("Ordre des partenaires mis à jour avec succès");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de l'ordre des partenaires");
      console.error(error);
    }
  };

  const movePartenaireDown = async (index) => {
    if (index === partenaires.length - 1) return;
    const newPartenaires = [...partenaires];
    const tmp = newPartenaires[index + 1];
    newPartenaires[index + 1] = newPartenaires[index];
    newPartenaires[index] = tmp;
    newPartenaires[index + 1].ordre++;
    newPartenaires[index].ordre--;
    setPartenaires(newPartenaires);
    try {
      await updatePartenaireOrder(newPartenaires);
      fetchPartenaires();
      toast.success("Ordre des partenaires mis à jour avec succès");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de l'ordre des partenaires");
      console.error(error);
    }
  };

  const defaultLogo = "/img/placeholder.png";

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-3 sm:px-6">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* Header dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                  Gestion des Partenaires
                </h1>
                <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                  Ajoutez, mettez à jour et organisez l’affichage de vos partenaires.
                </p>
              </div>
              <Button
                text="Ajouter un partenaire"
                icon={FaPlus}
                onClick={() => setIsCreateModalOpen(true)}
                className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-1"
                hoverLabel="Ajouter un nouveau partenaire"
              />
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 flex items-center gap-4 bg-white/60 dark:bg-slate-900/40"
                  >
                    <div className="h-16 w-16 rounded-md bg-slate-200 dark:bg-slate-700" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-2/5 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/3 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/4 bg-slate-200 dark:bg-slate-700 rounded" />
                    </div>
                    <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                ))}
              </div>
            )}

            {!loading && partenaires.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-xl bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun partenaire pour le moment
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Cliquez sur “Ajouter un partenaire” pour commencer.
                </p>
              </div>
            )}

            {!loading && partenaires.length > 0 && (
              <>
                {/* Desktop table */}
                <div className="hidden lg:block">
                  <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {["Logo", "Nom", "URL", "Ordre", "Affiché", "Page Détail", "Actions"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {partenaires.map((p, index) => (
                          <tr
                            key={p.id}
                            className="group transition-all duration-150 hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            {/* Logo + overlay au survol */}
                            <td className="px-4 py-3 align-middle">
                              <button
                                onClick={() => openUploadModal(p)}
                                className="relative inline-flex items-center justify-center"
                                title="Changer le logo"
                                type="button"
                              >
                                <div className="h-14 w-20 sm:h-16 sm:w-24 rounded-md bg-white/70 dark:bg-slate-900/40 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center">
                                  <img
                                    src={p.photo_url || defaultLogo}
                                    alt={p.nom}
                                    onError={(e) => {
                                      e.currentTarget.src = defaultLogo;
                                    }}
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                                <div className="pointer-events-none absolute inset-0 rounded-md flex items-center justify-center bg-slate-900/0 opacity-0 transition group-hover:bg-slate-900/40 group-hover:opacity-100">
                                  <span className="text-[11px] sm:text-xs font-medium text-white px-2 py-1 rounded-full bg-black/40">
                                    Changer le logo
                                  </span>
                                </div>
                              </button>
                            </td>

                            {/* Nom */}
                            <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {p.nom}
                            </td>

                            {/* URL */}
                            <td className="px-4 py-3 text-sm">
                              {p.link_url ? (
                                <a
                                  href={p.link_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline break-all"
                                >
                                  {p.link_url}
                                </a>
                              ) : (
                                <span className="text-slate-500">—</span>
                              )}
                            </td>

                            {/* Ordre + flèches */}
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              <div className="inline-flex items-center gap-2">
                                <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                                  {p.ordre}
                                </span>
                                <div className="flex flex-col -my-1">
                                  <button
                                    onClick={() => movePartenaireUp(index)}
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                    title="Monter"
                                    type="button"
                                  >
                                    <TbTriangleFilled />
                                  </button>
                                  <button
                                    onClick={() => movePartenaireDown(index)}
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                    title="Descendre"
                                    type="button"
                                  >
                                    <TbTriangleInvertedFilled />
                                  </button>
                                </div>
                              </div>
                            </td>

                            {/* Affiché (toggle simple via bouton) */}
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => handleToggleActive(p)}
                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition ${
                                  p.is_active
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                    : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                }`}
                                type="button"
                                title="Basculer l'affichage"
                              >
                                {p.is_active ? "Affiché" : "Masqué"}
                              </button>
                            </td>

                            {/* Page détail */}
                            <td className="px-4 py-3 text-lg">
                              {p.is_detail ? "✅" : "❌"}
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ButtonEdit onClick={() => openEditModal(p)} />
                                <ButtonDelete onClick={() => handleDeletePartenaire(p)} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile cards */}
                <div className="lg:hidden space-y-3">
                  {partenaires.map((p, index) => (
                    <div
                      key={`card-${p.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => openUploadModal(p)}
                          className="relative shrink-0"
                          title="Changer le logo"
                          type="button"
                        >
                          <div className="h-16 w-24 rounded-md bg-white/70 dark:bg-slate-900/40 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center">
                            <img
                              src={p.photo_url || defaultLogo}
                              alt={p.nom}
                              onError={(e) => (e.currentTarget.src = defaultLogo)}
                              className="h-full w-full object-contain"
                            />
                          </div>
                        </button>

                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              {p.nom}
                            </h3>
                            <div className="flex items-center gap-1">
                              <span className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                                {p.ordre}
                              </span>
                              <div className="flex flex-col -my-1">
                                <button
                                  onClick={() => movePartenaireUp(index)}
                                  className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                  title="Monter"
                                  type="button"
                                >
                                  <TbTriangleFilled />
                                </button>
                                <button
                                  onClick={() => movePartenaireDown(index)}
                                  className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                  title="Descendre"
                                  type="button"
                                >
                                  <TbTriangleInvertedFilled />
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="mt-1 text-sm">
                            {p.link_url ? (
                              <a
                                href={p.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                              >
                                {p.link_url}
                              </a>
                            ) : (
                              <span className="text-slate-500">Aucune URL</span>
                            )}
                          </div>

                          <div className="mt-3 flex items-center gap-2">
                            <button
                              onClick={() => handleToggleActive(p)}
                              className={`text-xs rounded-full px-2.5 py-1 ${
                                p.is_active
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                  : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                              }`}
                              type="button"
                            >
                              {p.is_active ? "Affiché" : "Masqué"}
                            </button>
                            <ButtonEdit onClick={() => openEditModal(p)} />
                            <ButtonDelete onClick={() => handleDeletePartenaire(p)} />
                            <button
                              onClick={() => openUploadModal(p)}
                              className="ml-auto text-xs underline decoration-dotted underline-offset-4 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
                              type="button"
                            >
                              Changer le logo
                            </button>
                          </div>

                          <div className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                            Page détail : {p.is_detail ? "✅" : "❌"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <PartenairesCarousel partenaires={partenaires} />
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <CreatePartenaireModal
          isActive={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshPartenaires={fetchPartenaires}
        />
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedPartenaire && (
        <EditPartenaireModal
          isActive={isEditModalOpen}
          onClose={closeEditModal}
          refreshPartenaires={fetchPartenaires}
          partenaireData={selectedPartenaire}
        />
      )}

      {/* Upload Logo Modal */}
      {isUploadModalOpen && selectedPartenaire && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeUploadModal}
          />
          <div className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
              <h2 className="text-white text-lg font-bold">
                Changer le logo — {selectedPartenaire.nom}
              </h2>
            </div>

            {/* Modal Body */}
            <div className="bg-white dark:bg-slate-900 p-6">
              <form onSubmit={handleUploadSubmit} className="space-y-5">
                {/* Preview actuel */}
                <div className="flex items-center gap-4">
                  <div className="h-16 w-24 rounded-md bg-white/70 dark:bg-slate-900/40 ring-2 ring-slate-200 dark:ring-slate-700 overflow-hidden flex items-center justify-center">
                    <img
                      src={selectedPartenaire.photo_url || defaultLogo}
                      alt="Logo actuel"
                      onError={(e) => (e.currentTarget.src = defaultLogo)}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    <p className="font-medium">Logo actuel</p>
                    <p className="text-xs opacity-80">Formats acceptés : JPG, PNG, WebP</p>
                  </div>
                </div>

                {/* File input */}
                <label htmlFor="upload-logo" className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                  Nouveau logo
                </label>
                <input
                  id="upload-logo"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="block w-full text-sm file:mr-4 file:rounded-xl file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:font-semibold file:text-slate-800 hover:file:bg-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100 dark:hover:file:bg-slate-700"
                />

                {/* Selected preview */}
                {selectedFile && (
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-24 rounded-md overflow-hidden ring-2 ring-violet-400/70 bg-white">
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Aperçu"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                      {selectedFile.name} — {(selectedFile.size / 1024).toFixed(0)} Ko
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    text="Fermer"
                    onClick={closeUploadModal}
                    icon={FaTimes}
                    className="gap-2 !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl"
                    type="button"
                  />
                  <Button
                    text={isUploading ? "Envoi..." : "Sauvegarder"}
                    type="submit"
                    icon={FaSave}
                    className="gap-2 !rounded-xl"
                    disabled={isUploading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </section>
  );
};

export default SettingsPartenaires;
